import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowRightIcon, ClipboardListIcon, FileTextIcon, HeartHandshakeIcon, ScaleIcon, ShieldCheckIcon } from 'lucide-react';
import { services } from '../data/services';
import { ServiceCard } from '../components/ServiceCard';
import { ConsultationCta } from '../components/ConsultationCta';
import { company } from '../data/company';
const HERO_IMAGE = 'https://cdn.magicpatterns.com/patterns/generated-images/1d68e9ac-3ccc-4be6-bee0-45695c50b727.jpg';
const FAMILY_IMAGE = 'https://cdn.magicpatterns.com/patterns/generated-images/d98bb839-d37e-415e-8f36-2ae938f2a0f2.jpg';
const OFFICE_IMAGE = 'https://cdn.magicpatterns.com/patterns/generated-images/60155fdb-f801-41eb-9eb6-14b766a88345.jpg';
const principles = [{
  icon: ShieldCheckIcon,
  title: 'Discretion first',
  body: 'Private matters stay private. Information is shared only with the people who need to be involved.'
}, {
  icon: ScaleIcon,
  title: 'Neutral and impartial',
  body: 'We act on the terms of the document and the interests of the client — not on family dynamics.'
}, {
  icon: ClipboardListIcon,
  title: 'Documented throughout',
  body: 'Every decision, distribution and expense is recorded and available for review or accounting.'
}, {
  icon: HeartHandshakeIcon,
  title: 'Personal attention',
  body: 'A small caseload by design, so each client and family receives direct, consistent contact.'
}];
const process = [{
  step: 'Initial conversation',
  body: 'We listen to the situation, review the governing documents and identify what responsibilities are involved.'
}, {
  step: 'Scope and engagement',
  body: 'We confirm which services apply, explain the fee structure in writing and put a clear engagement in place.'
}, {
  step: 'Administration',
  body: 'Assets are secured, records established, and the day-to-day work of the role is carried out on schedule.'
}, {
  step: 'Reporting and review',
  body: 'You receive regular written accountings, with the plan reviewed as circumstances change.'
}];
export function Home() {
  return <main data-id="element-248" data-mp="1ojziior86kc5">
      <section className="bg-navy-900" data-id="element-249" data-mp="1amszqu6i4lmx">
        <div className="mx-auto grid max-w-shell items-stretch gap-12 px-6 lg:grid-cols-2 lg:gap-16 lg:px-6" data-id="element-250" data-mp="1anun9y4pf0lj">
          <div className="flex flex-col justify-center py-16 lg:py-28" data-id="element-251" data-mp="1fm5wc6n6d72p">
            <p className="text-[12px] font-medium tracking-[0.22em] text-gold" data-id="element-252" data-mp="23ayods0oeus1">
              PROFESSIONAL FIDUCIARY SERVICES
            </p>
            <h1 className="mt-6 font-serif text-[44px] leading-[1.08] text-white sm:text-[56px]" data-id="element-253" data-mp="28ce6pcbgyn6q">
              Trusted Fiduciary Services for You and Your Family
            </h1>
            <p className="mt-7 max-w-xl text-[17px] leading-relaxed text-navy-200" data-id="element-254" data-mp="1yixci77f762j">
              Professional, discreet and dependable support for trusts, estates, personal affairs
              and the people who depend on them.
            </p>
            <div className="mt-10 flex flex-wrap gap-4" data-id="element-255" data-mp="flkb3dbvztb2">
              <Link to="/services" className="flex items-center gap-2 bg-gold px-7 py-4 text-[15px] font-medium text-navy-950 transition-colors duration-150 ease-out hover:bg-gold-light" data-id="element-256" data-mp="fk9474n5a5fq">
                Our Services
                <ArrowRightIcon className="h-4 w-4" aria-hidden="true" data-id="element-257" data-mp="kcaguro85y6a" />
              </Link>
              <Link to="/contact" className="border border-white/25 px-7 py-4 text-[15px] font-medium text-white transition-colors duration-150 ease-out hover:bg-white/10" data-id="element-258" data-mp="3sru7rk1fb4i6">
                Contact Us
              </Link>
            </div>
          </div>
          <div className="relative min-h-[320px] lg:min-h-full" data-id="element-259" data-mp="oa2cbqmp1j1">
            <img src={HERO_IMAGE} alt="A fiduciary reviewing trust documents at a table with an older couple" className="h-full w-full object-cover lg:absolute lg:inset-0" data-id="element-260" data-mp="qw7y5tyl6ym" />
          </div>
        </div>
      </section>

      <section className="bg-white" data-id="element-261" data-mp="27fpt089xu57z">
        <div className="mx-auto grid max-w-shell gap-14 px-6 py-24 lg:grid-cols-2 lg:items-center lg:gap-20" data-id="element-262" data-mp="2cczni2c6p72o">
          <div data-id="element-263" data-mp="2hbjewi9jrb2f">
            <p className="text-[12px] font-medium tracking-[0.22em] text-gold" data-id="element-264" data-mp="2hbt2jz74d64y">WHO WE ARE</p>
            <h2 className="mt-5 font-serif text-[38px] leading-[1.15] text-navy-900 sm:text-[44px]" data-id="element-265" data-mp="2mbfiwhi8v2hz">
              Professional Fiduciary Support With a Personal Approach
            </h2>
            <p className="mt-7 text-[17px] leading-relaxed text-ink/75" data-id="element-266" data-mp="2vbc0jz67v13j">
              {company.name} acts as trustee, conservator, agent under power of
              attorney and personal representative for individuals and families who need
              dependable administration of important responsibilities.
            </p>
            <p className="mt-5 text-[17px] leading-relaxed text-ink/75" data-id="element-267" data-mp="3a21eh9b2aub3">
              The work is practical: managing assets, paying bills, keeping records, coordinating
              care and communicating with beneficiaries. We take that on so families can focus on
              the people involved rather than the paperwork.
            </p>
            <dl className="mt-10 grid grid-cols-2 gap-8 border-t border-slate-200 pt-8" data-id="element-268" data-mp="yfr3r1taov30">
              <div data-id="element-269" data-mp="tg4u12codwyy">
                <dt className="text-[13px] text-ink/60" data-id="element-270" data-mp="ofgrpkizxtpf">Years in practice</dt>
                <dd className="mt-2 font-serif text-3xl text-navy-900" data-id="element-271" data-mp="ohjs8yhrz58e">20+</dd>
              </div>
              <div data-id="element-272" data-mp="1m4s8adz0bm30">
                <dt className="text-[13px] text-ink/60" data-id="element-273" data-mp="1h44k0025xjch">Matters administered</dt>
                <dd className="mt-2 font-serif text-3xl text-navy-900" data-id="element-274" data-mp="1h68mcdkeyaan">400+</dd>
              </div>
            </dl>
            <Link to="/about" className="mt-10 inline-flex items-center gap-2 text-[15px] font-medium text-navy-900 transition-colors duration-150 ease-out hover:text-gold-dark" data-id="element-275" data-mp="17eclwvuuu839">
              More About Us
              <ArrowRightIcon className="h-4 w-4" aria-hidden="true" data-id="element-276" data-mp="2015q5o7gdpi0" />
            </Link>
          </div>
          <div data-id="element-277" data-mp="3ub451twjfiqz">
            <img src={FAMILY_IMAGE} alt="An adult daughter sitting and talking with her elderly mother at home" className="h-[520px] w-full object-cover" data-id="element-278" data-mp="3ucp0rmul97qf" />
          </div>
        </div>
      </section>

      <section className="bg-mist" data-id="element-279" data-mp="3pj2ovww917vo">
        <div className="mx-auto max-w-shell px-6 py-24" data-id="element-280" data-mp="3pi1c9nzl4dbu">
          <div className="max-w-2xl" data-id="element-281" data-mp="3ambu60xiskce">
            <p className="text-[12px] font-medium tracking-[0.22em] text-gold" data-id="element-282" data-mp="3ala8z3msihgy">OUR SERVICES</p>
            <h2 className="mt-5 font-serif text-[38px] leading-[1.15] text-navy-900 sm:text-[44px]" data-id="element-283" data-mp="3fmzlknpnv375">
              Areas We Administer
            </h2>
            <p className="mt-6 text-[17px] leading-relaxed text-ink/75" data-id="element-284" data-mp="36khzc9a0aak5">
              Each engagement is defined in writing so everyone knows exactly which
              responsibilities we hold.
            </p>
          </div>
          <div className="mt-12 grid gap-6 md:grid-cols-2 lg:grid-cols-3" data-id="element-285" data-mp="14sbxfayvsrj5">
            {services.slice(0, 6).map(service => <ServiceCard key={service.slug} service={service} data-id="element-286" data-mp="1evh8q2b3cwus" />)}
          </div>
          <Link to="/services" className="mt-12 inline-flex items-center gap-2 border border-navy-900 px-7 py-4 text-[15px] font-medium text-navy-900 transition-colors duration-150 ease-out hover:bg-navy-900 hover:text-white" data-id="element-287" data-mp="19we1cb7n2ejn">
            View All Services
            <ArrowRightIcon className="h-4 w-4" aria-hidden="true" data-id="element-288" data-mp="1xm8ektvve54f" />
          </Link>
        </div>
      </section>

      <section className="bg-white" data-id="element-289" data-mp="1srck7n35v5o5">
        <div className="mx-auto max-w-shell px-6 py-24" data-id="element-290" data-mp="26p9fmg5vp618">
          <div className="max-w-2xl" data-id="element-291" data-mp="26mn98juq4wkl">
            <p className="text-[12px] font-medium tracking-[0.22em] text-gold" data-id="element-292" data-mp="22mmji326cyy8">
              HOW WE WORK
            </p>
            <h2 className="mt-5 font-serif text-[38px] leading-[1.15] text-navy-900 sm:text-[44px]" data-id="element-293" data-mp="cbi0x28ozvyx">
              Principles That Guide Every Engagement
            </h2>
          </div>
          <div className="mt-14 grid gap-x-12 gap-y-12 sm:grid-cols-2 lg:grid-cols-4" data-id="element-294" data-mp="wuuwxt6o65sv">
            {principles.map(({
            icon: Icon,
            title,
            body
          }) => <div key={title} data-id="element-295" data-mp="11qthf5ovn3o0">
                <Icon className="h-6 w-6 text-gold" aria-hidden="true" data-id="element-296" data-mp="16jcxqaey8cwi" />
                <h3 className="mt-5 font-serif text-[21px] text-navy-900" data-id="element-297" data-mp="1algl9xew77y5">{title}</h3>
                <p className="mt-3 text-[15px] leading-relaxed text-ink/75" data-id="element-298" data-mp="1ankk40xxpgrk">{body}</p>
              </div>)}
          </div>
        </div>
      </section>

      <section className="bg-navy-950" data-id="element-299" data-mp="iv3f1hd1859l">
        <div className="mx-auto grid max-w-shell gap-14 px-6 py-24 lg:grid-cols-[1fr_1.1fr] lg:items-center lg:gap-20" data-id="element-300" data-mp="iw52klb8ik87">
          <div data-id="element-301" data-mp="ruqjkc125r6v">
            <img src={OFFICE_IMAGE} alt="A fountain pen, leather portfolio and organized documents on a walnut desk" className="h-[420px] w-full object-cover" data-id="element-302" data-mp="rv8mfri873nr" />
          </div>
          <div data-id="element-303" data-mp="238kk1kne6q1h">
            <p className="text-[12px] font-medium tracking-[0.22em] text-gold" data-id="element-304" data-mp="23c8ecm0nvgh9">THE PROCESS</p>
            <h2 className="mt-5 font-serif text-[36px] leading-[1.15] text-white sm:text-[42px]" data-id="element-305" data-mp="2ddamrsqmdy6r">
              A Clear Path From First Call to Ongoing Administration
            </h2>
            <ol className="mt-10 space-y-8" data-id="element-306" data-mp="23mpsghhzbftn">
              {process.map(({
              step,
              body
            }, index) => <li key={step} className="flex gap-6" data-id="element-307" data-mp="1zm5uxcthua6z" data-mpi={index}>
                  <span className="font-serif text-[15px] text-gold" data-id="element-308" data-mp="1uo43o4lwz4kf">
                    {String(index + 1).padStart(2, '0')}
                  </span>
                  <div className="border-l border-white/12 pl-6" data-id="element-309" data-mp="1pn6lvt4tgcjg">
                    <h3 className="font-serif text-[21px] text-white" data-id="element-310" data-mp="1kqg4n2h9zawg">{step}</h3>
                    <p className="mt-2 max-w-xl text-[15px] leading-relaxed text-navy-200" data-id="element-311" data-mp="1gobdcaeauqcz">
                      {body}
                    </p>
                  </div>
                </li>)}
            </ol>
          </div>
        </div>
      </section>

      <section className="bg-white" data-id="element-312" data-mp="3azzyiub4jp5t">
        <div className="mx-auto max-w-shell px-6 py-24" data-id="element-313" data-mp="cnto279qn46f">
          <div className="grid gap-12 lg:grid-cols-[0.9fr_1.1fr] lg:gap-20" data-id="element-314" data-mp="cqfug3kw7dn2">
            <div data-id="element-315" data-mp="8mgk62q7hb25">
              <p className="text-[12px] font-medium tracking-[0.22em] text-gold" data-id="element-316" data-mp="8p2qy059vmjb">
                COMMON QUESTIONS
              </p>
              <h2 className="mt-5 font-serif text-[36px] leading-[1.15] text-navy-900" data-id="element-317" data-mp="hn6aiqwp698h">
                What families usually ask first
              </h2>
              <p className="mt-6 text-[16px] leading-relaxed text-ink/70" data-id="element-318" data-mp="3q3re1s6k8a47">
                If your question is not here, a short conversation is usually the fastest way to
                get a clear answer.
              </p>
              <Link to="/contact" className="mt-8 inline-flex items-center gap-2 text-[15px] font-medium text-navy-900 transition-colors duration-150 ease-out hover:text-gold-dark" data-id="element-319" data-mp="3v2kqdha41v2s">
                Ask us directly
                <ArrowRightIcon className="h-4 w-4" aria-hidden="true" data-id="element-320" data-mp="32ddpibn6oted" />
              </Link>
            </div>
            <dl className="divide-y divide-slate-200 border-t border-slate-200" data-id="element-321" data-mp="1htrtzuu6gn9y">
              {[{
              q: 'When does a family need a professional fiduciary?',
              a: 'Usually when there is no suitable individual to serve, when the responsibilities have become too complex or time-consuming, or when neutrality between family members is important.'
            }, {
              q: 'How are fees determined?',
              a: 'Fees are based on the scope of the role and the work involved, disclosed in writing before the engagement begins and reported in every accounting.'
            }, {
              q: 'Do you replace our attorney or accountant?',
              a: 'No. We work alongside existing counsel, CPAs and investment advisors and coordinate the administrative work between them.'
            }, {
              q: 'How often will we hear from you?',
              a: 'On a set schedule appropriate to the matter, plus immediate contact whenever something requires a decision or changes materially.'
            }].map(({
              q,
              a
            }) => <div key={q} className="py-7" data-id="element-322" data-mp="2d2vikcah6mmf">
                  <dt className="font-serif text-[20px] text-navy-900" data-id="element-323" data-mp="29464ehhm511q">{q}</dt>
                  <dd className="mt-3 text-[16px] leading-relaxed text-ink/75" data-id="element-324" data-mp="2440byz3irail">{a}</dd>
                </div>)}
            </dl>
          </div>
        </div>
      </section>

      <ConsultationCta data-id="element-325" data-mp="1u640u7al0fkh" />
    </main>;
}